import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@mzic/mzic-environments';
import {
  ArtistType,
  RegistryTypeEnum,
  ReleaseBulkResourceForm,
  ReleaseReadResponse,
  ReleaseResourceData,
  ReleaseResourceForm,
  ResponseInterface,
} from '@mzic/mzic-interfaces';
import { deleteNullProperties } from '@mzic/mzic-utils';
import { map, Observable } from 'rxjs';
import { MzicArtistLocalService } from '../../mzic-artist-local/mzic-artist-local.service';
import { ArtistTypeService } from '../artist-type/artist-type.service';

@Injectable({
  providedIn: 'root',
})
export class ReleaseService {
  private apiUrl = environment.apiUrl;

  mzicArtistLocalService = inject(MzicArtistLocalService);
  artistTypeService = inject(ArtistTypeService);

  constructor(private readonly _http: HttpClient) {}

  createRelease(
    data: Partial<ReleaseResourceForm>,
  ): Observable<ResponseInterface<ReleaseResourceData>> {
    // Garante de excluir propriedades que estão nulas (para não quebrar a API)
    deleteNullProperties(data);

    return this._http.post<ResponseInterface<ReleaseResourceData>>(
      `${this.apiUrl}/api/distribution/release/v1/create`,
      data,
    );
  }

  /**
   * Essa função é usada para criar um import com vários releases, ela tem um tratamento específico para preencher o artista principal
   */
  createBulkRelease(
    data: Partial<ReleaseBulkResourceForm>[],
    artistType: ArtistType | null,
  ): Observable<ResponseInterface<ReleaseResourceData>> {
    // Captura o artista principal da account
    const { registryType } = this.mzicArtistLocalService.getWalletTeam().team;

    //  Caso específico, onde vamos criar o artista principal
    if (registryType === RegistryTypeEnum.ARTIST) {
      data = this.adaptMainArtistForArtistAccount(data, artistType);
    }

    return this._http.post<ResponseInterface<ReleaseResourceData>>(
      `${this.apiUrl}/api/distribution/release/v1/create/bulk`,
      data,
    );
  }

  /**
   * Essa função é usada para atribuir o artista principal
   * @param data Os dados para criar o import
   * @param artistType O objeto do tipo artista
   * @returns Retorna com os dados preenchendo o artista principal
   */
  private adaptMainArtistForArtistAccount(
    data: Partial<ReleaseBulkResourceForm>[],
    artistType: ArtistType | null,
  ): Partial<ReleaseBulkResourceForm>[] {
    if (!artistType) {
      console.log(
        '🚨 Artist type not found: não foi possível encontrar o artistType para atribuir o artista principal',
      );
      return data;
    }

    return data.map((release) => {
      const artists = release.artists ?? [];
      const { artistId } = this.mzicArtistLocalService.getWalletTeam().team;

      if (artists.length === 0) {
        // Adiciona o artista principal
        artists.push({
          artistTypeId: artistType.id,
          artistId: artistId,
          mainArtist: true,
        });

        // Percorre as tracks e adiciona o artista principal
        release.tracks?.map((track) => {
          const artists = track.artists ?? [];

          if (artists.length === 0) {
            artists.push({
              artistTypeId: artistType.id,
              artistId: artistId,
              mainArtist: true,
            });
          }

          track.artists = artists;
          return track;
        });
      }

      release.artists = artists;
      return release;
    });
  }

  /**
   *
   * @returns Retorna o tipo de artista do tipo ROLE_ID = 49
   */
  loadArtistType() {
    return this.artistTypeService
      .getArtistTypeIdByRoleId(49)
      .pipe(
        map((item) =>
          item.data.content.length > 0 ? item.data.content[0] : null,
        ),
      );
  }

  updateRelease(
    data: Partial<ReleaseResourceForm>,
    id: number,
  ): Observable<ResponseInterface<ReleaseReadResponse>> {
    // Garante de excluir propriedades que estão nulas (para não quebrar a API)
    deleteNullProperties(data);

    // Não pode enviar o id no corpo da requisição
    delete data.id;

    return this._http.put<ResponseInterface<ReleaseReadResponse>>(
      `${this.apiUrl}/api/distribution/release/v1/update/${id}`,
      data,
    );
  }

  getReleaseById(
    id: number,
  ): Observable<ResponseInterface<ReleaseReadResponse>> {
    return this._http.get<ResponseInterface<ReleaseReadResponse>>(
      `${this.apiUrl}/api/distribution/release/v1/find/${id}`,
    );
  }
}
